/**
 * MenuItem.js — одна строка бокового меню (иконка + название + стрелка).
 *
 * Отображается для каждого пункта из menuRouteList (Routes.js):
 * «Личный кабинет», «Игры», «Поддержка» и т.д.
 *
 * По клику — navigate(path). Красная полоска слева = текущая страница.
 */

import { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { useLocation, useNavigate } from "react-router-dom";
import {
  color_grey_dark,
  color_grey_ultra_light,
  color_red_default,
  color_white,
} from "../../constants/colors";
import { useWindowDimensions } from "../../utils";
import ArrowIcon from "../icons/ArrowIcon";
import ArrowIconLeft from "../icons/ArrowIconLeft";
import { OnestNormalDefault } from "../styled/TextComponents";
import { useI18n } from "../../i18n/I18nProvider";
import { isDesktopMenuLayout, isMenuItemSelected } from "./navBarUtils";

const MenuItem = ({ icon, path, name }) => {
  const { t } = useI18n();
  const user = useSelector((state) => state.user);
  const location = useLocation();
  const navigate = useNavigate();
  const { height: screenHeight, width: screenWidth } = useWindowDimensions();

  const [isHovered, setIsHovered] = useState(false);
  const [isSelected, setIsSelected] = useState(false);
  const [color, setColor] = useState(color_grey_dark);

  const desktopMenu = isDesktopMenuLayout(location.pathname, screenWidth);
  const isUpdatesRoute = path === "/service/updates";

  useEffect(() => {
    const selected = isMenuItemSelected(location.pathname, path);
    setIsSelected(selected);
    setColor(selected || name === "Выйти" ? color_red_default : color_grey_dark);
  }, [location.pathname, path, name]);

  return (
    <div
      onClick={() => navigate(path)}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      style={{
        display: "flex",
        height: screenHeight < 660 ? 45 : 50,
        width: 240,
        flexDirection: desktopMenu ? "row" : "row-reverse",
        justifyContent: "flex-start",
        alignItems: "center",
        gap: 20,
        backgroundColor: isHovered ? color_grey_ultra_light : "transparent",
        borderRadius: 8,
        cursor: "pointer",
        transition: "background-color 0.2s ease",
      }}
    >
      {/* Красная полоска слева — индикатор активного пункта */}
      <div
        style={{
          height: screenHeight < 660 ? 45 : 50,
          width: 3,
          backgroundColor: isSelected ? color : "transparent",
        }}
      />

      <span style={{ position: "relative", color, fontSize: 16 }}>
        {/* Badge непрочитанных «Обновлений» */}
        {isUpdatesRoute && user.unread_changes_count > 0 ? (
          <span
            style={{
              position: "absolute",
              right: -10,
              top: -10,
              fontSize: 10,
              backgroundColor: color_red_default,
              borderRadius: 12,
              padding: "2px 6px",
              color: color_white,
              fontFamily: "Onest",
            }}
          >
            {user.unread_changes_count}
          </span>
        ) : null}
        {typeof icon === "function"
          ? icon({ fill: isSelected ? color_red_default : color_grey_dark })
          : icon}
      </span>

      <OnestNormalDefault style={{ color }}>{t(name)}</OnestNormalDefault>

      {desktopMenu ? (
        <ArrowIcon fill={isSelected ? color : "transparent"} />
      ) : (
        <ArrowIconLeft fill={isSelected ? color : "transparent"} />
      )}
    </div>
  );
};

export default MenuItem;
